import { useEffect, useState } from "react";
import CustomInputField from "../CustomInputField/CustomInputField";
import {
  base64ToFile,
  blobToFile,
  convertFileToBase64,
  isValidFileType,
  removeMetadataFromFile,
} from "../../utils/helpers";
import { errorToast } from "../../utils/makeToast";
import PdfIcon from "../../assets/icons/PdfIcon";
import CloseIcon from "../../assets/icons/CloseIcon";
import styles from "./styles.module.css";
import UploadIcon from "../../assets/icons/UploadIcon";
import useDragAndDropFiles from "../../customHooks/useDragAndDrop";
import apiHooks from "../../customHooks/apis";
import LoadingSpinner from "../LoadingSpinner";
import {
  MAX_FILE_UPLOAD_SIZE_FIGURE,
  MAX_FILE_UPLOAD_SIZE_TEXT,
} from "../../utils/constants";
import InputErrorMessage from "../InputErrorMessage";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { saveStepperData } from "../../redux/stepperSlice";

interface DocUploadProps {
  label: string;
  name: string;
  bottomText: string;
  errorMessage: string;
  getFileUrl: (name: string, url: string) => void;
  show: boolean;
  required: boolean;
  isFaceMatch?: boolean;
  matchingFace?: boolean;
  disabled?: boolean;
  faceMatchSuccess?: boolean;
  defaultImageUrl?: string;
  handleBvnFaceMatch?: (file: File) => void;
  setIsUploading?: (state: boolean) => void;
}
const DocUpload: React.FC<Partial<DocUploadProps>> = ({
  label,
  bottomText,
  errorMessage,
  show,
  required,
  isFaceMatch,
  matchingFace,
  faceMatchSuccess,
  getFileUrl,
  handleBvnFaceMatch,
  setIsUploading,
  disabled,
  name = "",
  defaultImageUrl,
}) => {
  const dispatch = useAppDispatch();
  const stepperData = useAppSelector((state) => state.stepper.data);
  const { useUploadFile, useUserDetails } = apiHooks;
  const { uploadingFile, handleUploadFile } = useUploadFile();
  const { userDetails } = useUserDetails();
  const {
    handleDragLeave,
    handleDragOver,
    handleDrop,
    setFiles,
    files: dragFiles,
    isDragging,
  } = useDragAndDropFiles();
  const [doc, setDoc] = useState<File | null>(null);
  const [isError, setIsError] = useState<boolean>(false);
  const documentName = `${userDetails.firstName || ""} ${
    userDetails?.middleName || ""
  } ${userDetails?.lastName || ""}`.trim();
  const uploadedFileName = `${name}DocUpload`;

  const returnImageFileToUrl = (file?: File): string => {
    if (!file) return "";
    const docIsImage = file.type.includes("image");
    if (!docIsImage) return "";
    return URL.createObjectURL(file);
  };

  const imageFileToUrl = returnImageFileToUrl(doc);

  const handleFileUrl = (url: string, file: File) => {
    if (!url) {
      handleSavebase64Image("");
      setDoc(null);
      getFileUrl?.(name, "");
      return;
    }
    getFileUrl?.(name, url);
    setDoc(file);
  };

  const handleSavebase64Image = (url: string) => {
    dispatch(saveStepperData({ [uploadedFileName]: url }));
  };

  const processFile = (filesArr: File[] | FileList) => {
    setIsError(false);
    if (!!filesArr.length) {
      const file = filesArr[0];
      const fileSizeInKb = file.size / 1_000;
      if (fileSizeInKb > MAX_FILE_UPLOAD_SIZE_FIGURE) {
        errorToast(
          `File size exceeds ${MAX_FILE_UPLOAD_SIZE_TEXT.toUpperCase()}. Please upload a smaller file.`,
        );
        return setIsError(true);
      }
      if (isValidFileType(file)) {
        isError && setIsError(false);
        removeMetadataFromFile(file, async (blob) => {
          if (isFaceMatch && handleBvnFaceMatch) {
            return handleBvnFaceMatch(blobToFile(blob, documentName));
          }
          const imageStr = await convertFileToBase64(blob);
          handleUploadFile(
            {
              postedFilebase64: imageStr,
              filetype: file.type,
              documentName: file.name,
            },
            (fileUrl) => handleFileUrl(fileUrl, blobToFile(blob, documentName)),
          );
          handleSavebase64Image(imageStr);
        });
      } else
        bottomText
          ? setIsError(true)
          : errorToast(errorMessage || "Invalid file type");
    } else errorToast("Please select a file");
  };

  const handleDocChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files && !!files.length) {
      processFile(files);
    }
  };

  const handleRemoveFile = () => {
    if (doc.type.includes("image")) {
      handleSavebase64Image("");
    }
    setDoc(null);
    setFiles([]);
    getFileUrl?.(name, "");
  };

  useEffect(() => {
    if (!isDragging) {
      setFiles([]);
    }
    if (!!dragFiles.length) {
      processFile(dragFiles);
    }
  }, [isDragging]);

  useEffect(() => {
    if (!faceMatchSuccess) {
      setDoc(null);
    }
  }, [faceMatchSuccess, matchingFace]);

  useEffect(() => {
    setIsUploading && setIsUploading(uploadingFile);
  }, [uploadingFile]);

  useEffect(() => {
    if (uploadingFile || !defaultImageUrl || !!imageFileToUrl) return;
    setDoc(base64ToFile(stepperData[uploadedFileName], documentName));
  }, [defaultImageUrl, uploadingFile, imageFileToUrl, documentName]);

  if (!show) return <></>;
  if (uploadingFile || matchingFace) return <LoadingSpinner isRed />;
  return (
    <div className={`${disabled ? "pe-none" : ""} d-flex flex-column w-100`}>
      {label && !doc && (
        <small className={styles.docUploadLabel} title={label}>
          {label} {required && <strong className="text-danger">*</strong>}
        </small>
      )}
      {doc ? (
        <div
          className={`${styles.docWrapper} position-relative`}
          title={doc.name}
        >
          {imageFileToUrl ? (
            <img src={imageFileToUrl} className="rounded" alt={doc.name} />
          ) : (
            <>
              <PdfIcon className={styles.pdf_icon} />
              <small title={doc.name} className={styles.docName}>
                {doc.name}
              </small>
            </>
          )}
          <button
            type="button"
            className="position-absolute top-0 end-0 bg-transparent border border-0"
            onClick={handleRemoveFile}
          >
            <CloseIcon fill="red" />
          </button>
        </div>
      ) : (
        <>
          <label
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`${styles.inputLabel} ${
              isDragging ? "border-success" : isError ? "border-danger" : ""
            } pic_upload d-flex justify-content-between pt-3 pr-3 px-2 w-100`}
          >
            <small className="text-nowrap">
              Click to upload or drag <br />
              and drop file
            </small>
            <CustomInputField
              type="file"
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={handleDocChange}
              className="d-none"
            />
            <UploadIcon />
          </label>
          {bottomText && (
            <small className={`${isError ? "text-danger" : ""} small_text`}>
              {bottomText}
            </small>
          )}
          <InputErrorMessage errorMessage={errorMessage} />
        </>
      )}
    </div>
  );
};

export default DocUpload;
